import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Stack,
  Group,
  Select,
  SimpleGrid,
  NumberInput,
  Text,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  CommissionRecord,
  CreateCommissionInput,
  CalculationMethod,
  CommissionTierDrafts,
} from "../../types/commission";
import CommissionInput from "./CommissionInput";
import TieredCommissionBuilder from "./TieredCommissionBuilder";
import InfoTooltip from "../common/InfoTooltip/InfoTooltip";
import {
  CALC_METHOD_OPTIONS,
  CalcMethodTooltip,
  formatDateToString,
  parseDateString,
  toDisplayValue,
} from "./commissionFormHelpers";
import ServiceCommissionField from "./commissions/ServiceCommissionField";
import ServiceSpecificRatesScreen from "./commissions/ServiceSpecificRatesScreen";
import { useServiceOverrideDrafts } from "./commissions/useServiceOverrideDrafts";
import { buildCommissionInput } from "./commissions/buildCommissionInput";
import {
  CommissionFormActions,
  CommissionFormFrame,
  CommissionFormScreen,
  inlineFormFrame,
} from "./commissions/commissionFormFrame";
import { isServiceSpecific } from "./commissions/commissionDisplay";
import { isServiceCommissionOverridesEnabled } from "../../utils/serviceCommissionOverridesFlag";
import { wireToDrafts } from "../../utils/commissionUtils";

const EFFECTIVE_DATE_HELP =
  "Any previous commission rate ends the day before this date, and this rate takes over from then on.";

/** Lets a host save the form from outside it (e.g. an unsaved-changes
 * dialog's "Save"). */
export interface CommissionFormController {
  /** Validates and saves, exactly like the Save button. Resolves true once
   * `onSubmit` resolves; false when validation fails or `onSubmit` rejects. */
  submit: () => Promise<boolean>;
}

interface CommissionFormProps {
  staffId: string;
  existingCommission?: CommissionRecord | null;
  /** Saves the payload. Reject to signal the save failed (the form stays). */
  onSubmit: (data: CreateCommissionInput) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  /** Hide the "Effective start date" field. Used for tier commission templates,
   * which have no date — the date is set to "today" when applied to staff. */
  hideEffectiveDate?: boolean;
  /** Override the primary button label (defaults to Save/Update Commission). */
  submitLabel?: string;
  /** Override the secondary button label (defaults to Cancel/Back). */
  cancelLabel?: string;
  /** Render read-only: disable inputs and hide the submit button. */
  disabled?: boolean;
  /** Hide the secondary (cancel/back) button entirely. */
  hideCancel?: boolean;
  /** Node rendered at the left of the footer row (e.g. a "Remove" action). */
  leftSection?: React.ReactNode;
  /** Disable (but keep visible) the submit button and lock the inputs while a
   * sibling action is in flight (e.g. a "Remove" running in the footer). */
  submitDisabled?: boolean;
  /** Adding a new effective-dated rate: start from this record's terms (its
   * method, rates and per-service rates), dated today. */
  prefillFrom?: CommissionRecord | null;
  /** Lays out each screen (defaults to stacked in place). */
  frame?: CommissionFormFrame;
  /** Called when the form gains or loses unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Filled with a {@link CommissionFormController} while mounted. */
  controllerRef?: React.Ref<CommissionFormController>;
}

/** The single service rate to show. A service-specific record stores 0% as
 * its default — that isn't a rate the user chose, so the box starts empty. */
const initialServiceRate = (seed: CommissionRecord | null): string =>
  seed && !(isServiceSpecific(seed) && !seed.service_amount)
    ? toDisplayValue(seed.service_amount, seed.service_amount_type)
    : "";

/** A record saved with a non-zero default AND per-service rates (via the API)
 * paid every other service that default; the service-specific screen
 * pre-fills it so saving (default → 0%) doesn't cut pay. */
const legacyDefaultOf = (seed: CommissionRecord | null): string | null =>
  seed && isServiceSpecific(seed) && (seed.service_amount ?? 0) > 0
    ? toDisplayValue(seed.service_amount, seed.service_amount_type)
    : null;

const NewCommissionForm: React.FC<CommissionFormProps> = ({
  staffId,
  existingCommission,
  onSubmit,
  onCancel,
  isLoading,
  hideEffectiveDate = false,
  submitLabel,
  cancelLabel,
  disabled = false,
  hideCancel = false,
  leftSection,
  submitDisabled = false,
  prefillFrom,
  frame = inlineFormFrame,
  onDirtyChange,
  controllerRef,
}) => {
  // The record whose terms the form starts from: the one being edited, or the
  // current rate a new one replaces.
  const seed = existingCommission ?? prefillFrom ?? null;
  // Lock every input while read-only, while a save is in flight, or while a
  // sibling action (e.g. Remove) is running.
  const fieldsDisabled = disabled || isLoading || submitDisabled;
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>(
    seed?.calculation_method || "STANDARD",
  );
  const [effectiveStartDate, setEffectiveStartDate] = useState<Date | null>(
    existingCommission?.effective_start_date
      ? parseDateString(existingCommission.effective_start_date)
      : new Date(),
  );
  const [serviceCommission, setServiceCommission] = useState(() =>
    initialServiceRate(seed),
  );
  const [productCommission, setProductCommission] = useState(
    seed ? toDisplayValue(seed.product_amount, seed.product_amount_type) : "",
  );
  const [numServicesCutoff, setNumServicesCutoff] = useState<number | "">(
    seed?.num_services_cutoff ?? "",
  );
  const [tierDrafts, setTierDrafts] = useState<CommissionTierDrafts>(
    wireToDrafts(seed?.tiers),
  );

  // Service-specific rates (flag-gated). Never used with TIERED: saving a
  // Tiered rate clears them. In this mode the default service rate is saved
  // as 0%: an empty box earns nothing.
  const overridesEnabled = isServiceCommissionOverridesEnabled();
  const serviceOverrides = useServiceOverrideDrafts(seed?.service_overrides);
  const serviceSpecific = overridesEnabled && serviceOverrides.enabled;
  const [screen, setScreen] = useState<CommissionFormScreen>("form");
  const [legacyDefault, setLegacyDefault] = useState<string | null>(() =>
    legacyDefaultOf(seed),
  );

  const [serviceError, setServiceError] = useState<string | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Unsaved changes: anything the user can edit differs from how the form
  // opened.
  const snapshot = JSON.stringify([
    calculationMethod,
    effectiveStartDate ? formatDateToString(effectiveStartDate) : null,
    serviceCommission,
    productCommission,
    numServicesCutoff,
    tierDrafts,
    serviceOverrides.enabled,
    serviceOverrides.drafts,
  ]);
  const initialSnapshot = useRef(snapshot);
  const dirty = snapshot !== initialSnapshot.current;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const handleSubmit = async (): Promise<boolean> => {
    setFormError(null);
    const result = buildCommissionInput({
      staffId,
      recordId: existingCommission?.id,
      calculationMethod,
      requireDate: !hideEffectiveDate,
      effectiveStartDate,
      serviceCommission,
      productCommission,
      numServicesCutoff,
      tierDrafts,
      serviceSpecific,
      overrides: overridesEnabled ? serviceOverrides.toPayload() : null,
      hasFieldErrors: !!(serviceError || productError),
    });
    if (result.error !== null) {
      setFormError(result.error);
      return false;
    }
    try {
      await onSubmit(result.input);
      return true;
    } catch {
      return false;
    }
  };
  useImperativeHandle(controllerRef, () => ({ submit: handleSubmit }));

  const useOneRate = (): void => {
    serviceOverrides.setEnabled(false);
    serviceOverrides.setDrafts({});
    setLegacyDefault(null);
  };

  if (screen === "service-rates") {
    return (
      <ServiceSpecificRatesScreen
        staffId={staffId}
        value={serviceOverrides.drafts}
        onChange={serviceOverrides.setDrafts}
        // Switching from one rate starts every service at that rate.
        fillEmptyWith={
          legacyDefault ?? (serviceSpecific ? null : serviceCommission)
        }
        savedOverrides={existingCommission?.service_overrides}
        onServiceNames={serviceOverrides.setServiceNames}
        disabled={fieldsDisabled}
        frame={frame}
        onDone={() => {
          serviceOverrides.setEnabled(true);
          setLegacyDefault(null);
          setScreen("form");
        }}
      />
    );
  }

  const methodSelect = (
    <Select
      label={
        <Group
          gap={4}
          align="center"
          component="span"
          style={{ display: "inline-flex" }}
        >
          Calculation method
          <Tooltip
            label={<CalcMethodTooltip />}
            maw={340}
            multiline
            withArrow
            position="bottom"
          >
            <IconInfoCircle
              size={14}
              color="var(--mantine-color-dimmed)"
              style={{ cursor: "help" }}
            />
          </Tooltip>
        </Group>
      }
      withAsterisk
      data={CALC_METHOD_OPTIONS}
      value={calculationMethod}
      onChange={(val) => {
        const method = (val as CalculationMethod) || "STANDARD";
        setCalculationMethod(method);
        setServiceError(null);
        setProductError(null);
        setFormError(null);
        if (method !== "NUM_SERVICES") {
          setNumServicesCutoff("");
        }
      }}
      allowDeselect={false}
      disabled={fieldsDisabled}
    />
  );

  const dateInput = !hideEffectiveDate && (
    <DateInput
      label={
        <Group
          gap={4}
          align="center"
          component="span"
          style={{ display: "inline-flex" }}
        >
          Effective start date
          <InfoTooltip
            ariaLabel="About the effective start date"
            label={EFFECTIVE_DATE_HELP}
            size={14}
          />
        </Group>
      }
      withAsterisk
      placeholder="Select date"
      value={effectiveStartDate}
      disabled={fieldsDisabled}
      onChange={(val) => {
        if (!val) {
          setEffectiveStartDate(null);
          return;
        }
        // Mantine DateInput creates dates at UTC midnight. Extract
        // the UTC components to build a local midnight Date so
        // formatDateToString (which uses local getters) stays correct.
        const d = new Date(val);
        setEffectiveStartDate(
          new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
        );
      }}
    />
  );

  const footer = (
    <CommissionFormActions
      leftSection={leftSection}
      cancelLabel={
        hideCancel
          ? undefined
          : (cancelLabel ?? (existingCommission ? "Cancel" : "Back"))
      }
      onCancel={onCancel}
      submitLabel={
        disabled
          ? undefined
          : (submitLabel ??
            (existingCommission ? "Update Commission" : "Save Commission"))
      }
      onSubmit={() => {
        void handleSubmit();
      }}
      isLoading={isLoading}
      submitDisabled={submitDisabled}
    />
  );

  return frame({
    screen: "form",
    footer,
    children: (
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: dateInput ? 2 : 1 }} spacing="sm">
          {methodSelect}
          {dateInput}
        </SimpleGrid>

        {calculationMethod === "NUM_SERVICES" && (
          <NumberInput
            label="Threshold (number of services)"
            placeholder="e.g. 4"
            min={1}
            value={numServicesCutoff}
            onChange={(val) =>
              setNumServicesCutoff(val === "" ? "" : Number(val))
            }
            required
            disabled={fieldsDisabled}
          />
        )}

        {calculationMethod === "TIERED" ? (
          <TieredCommissionBuilder
            value={tierDrafts}
            onChange={setTierDrafts}
            disabled={fieldsDisabled}
          />
        ) : (
          <>
            <ServiceCommissionField
              value={serviceCommission}
              onChange={setServiceCommission}
              onError={setServiceError}
              error={serviceError || undefined}
              disabled={fieldsDisabled}
              allowServiceSpecific={overridesEnabled}
              serviceSpecific={serviceSpecific}
              previewRates={serviceOverrides.previewRates}
              onOpenServiceRates={() => setScreen("service-rates")}
              onUseOneRate={useOneRate}
            />

            <CommissionInput
              label="Product commission"
              placeholder="e.g. 10% or $5 — leave empty for none"
              value={productCommission}
              onChange={setProductCommission}
              onError={setProductError}
              error={productError || undefined}
              disabled={fieldsDisabled}
            />
          </>
        )}

        {formError && (
          <Text size="sm" c="red">
            {formError}
          </Text>
        )}
      </Stack>
    ),
  });
};

export default NewCommissionForm;
